import { IconButton, Icons, Input } from '@healthinal/ui';
import type { ComponentProps, KeyboardEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Control, Controller, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form';
import {
  formatNumericDateInput,
  formatNumericDateInputDraft,
  getNumericDateInputPlaceholder,
  isInputDateValue,
  isNumericDateInputDraftValid,
  parseDateInputValue,
  parseNumericDateInput,
} from '../../utils/date.ts';
import { GermanDateCalendar } from './GermanDateCalendar.tsx';

type GermanDateInputVariant = ComponentProps<typeof Input>['variant'];
type GermanDateInputMode = 'freeText' | 'calendar' | 'formatted';
type CalendarStyle = ComponentProps<'div'>['style'];

const CALENDAR_WIDTH = 280;
const ESTIMATED_CALENDAR_HEIGHT = 320;
const CALENDAR_GAP = 4;
const VIEWPORT_PADDING = 8;
const CALENDAR_LISTBOX_SELECTOR = '[data-german-date-calendar-listbox="true"]';
const NUMERIC_DATE_ALLOWED_KEYS = new Set([
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'Tab',
  'Enter',
  'Escape',
]);
function formatDisplayDate(value: string) {
  return formatNumericDateInput(value);
}

function isDateWithinRange(value: string, min?: string, max?: string) {
  return (!min || value >= min) && (!max || value <= max);
}

function isDateInputOutOfRange(value: string, min?: string, max?: string) {
  const dateValue = parseDateInputValue(value);

  return !!dateValue && !isDateWithinRange(dateValue, min, max);
}

function withDateRangeValidation<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  rules: RegisterOptions<TFieldValues, TFieldName> | undefined,
  min?: string,
  max?: string,
): RegisterOptions<TFieldValues, TFieldName> | undefined {
  if (!min && !max) return rules;

  const validateDateRange = (value: unknown) => {
    if (typeof value !== 'string') return true;

    return !isDateInputOutOfRange(value, min, max) || 'range';
  };

  if (typeof rules?.validate === 'function') {
    return {
      ...rules,
      validate: {
        custom: rules.validate,
        dateRange: validateDateRange,
      } as RegisterOptions<TFieldValues, TFieldName>['validate'],
    };
  }

  return {
    ...rules,
    validate: {
      ...(rules?.validate ?? {}),
      dateRange: validateDateRange,
    } as RegisterOptions<TFieldValues, TFieldName>['validate'],
  };
}

interface GermanDateInputProps<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control?: Control<TFieldValues>;
  name?: TFieldName;
  rules?: RegisterOptions<TFieldValues, TFieldName>;
  value?: string;
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  variant?: GermanDateInputVariant;
  onValueBlur?: (value: string) => void;
  dateInputMode?: GermanDateInputMode;
  showClearButton?: boolean;
}

export function GermanDateInput<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  rules,
  value: controlledValue,
  onChange: onControlledChange,
  min,
  max,
  disabled,
  variant = 'outlined',
  onValueBlur,
  dateInputMode = 'formatted',
  showClearButton = false,
}: GermanDateInputProps<TFieldValues, TFieldName>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [calendarStyle, setCalendarStyle] = useState<CalendarStyle>();
  const [textValueMode, setTextValueMode] = useState<'date' | 'freeText'>('date');
  const [formattedDraftValue, setFormattedDraftValue] = useState<string>();
  const updateCalendarPosition = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const belowTop = rect.bottom + CALENDAR_GAP;
    const aboveTop = rect.top - ESTIMATED_CALENDAR_HEIGHT - CALENDAR_GAP;
    const hasSpaceBelow = belowTop + ESTIMATED_CALENDAR_HEIGHT <= window.innerHeight - VIEWPORT_PADDING;
    const top = hasSpaceBelow || aboveTop < VIEWPORT_PADDING ? belowTop : Math.max(VIEWPORT_PADDING, aboveTop);
    const maxLeft = Math.max(VIEWPORT_PADDING, window.innerWidth - CALENDAR_WIDTH - VIEWPORT_PADDING);
    const left = Math.min(Math.max(VIEWPORT_PADDING, rect.left), maxLeft);

    setCalendarStyle({
      position: 'fixed',
      top,
      left,
      zIndex: 1600,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setCalendarStyle(undefined);
      return;
    }

    updateCalendarPosition();
  }, [open, updateCalendarPosition]);

  useEffect(() => {
    if (!open) return;

    window.addEventListener('resize', updateCalendarPosition);
    window.addEventListener('scroll', updateCalendarPosition, true);

    return () => {
      window.removeEventListener('resize', updateCalendarPosition);
      window.removeEventListener('scroll', updateCalendarPosition, true);
    };
  }, [open, updateCalendarPosition]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (calendarRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest(CALENDAR_LISTBOX_SELECTOR)) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const renderInput = (value: string, onChange: (value: string) => void, onBlur: () => void = () => undefined) => {
    const isFormattedDateMode = dateInputMode === 'formatted';
    const isFreeTextMode = dateInputMode === 'freeText';
    const isCalendarMode = dateInputMode === 'calendar';
    const isTextEditable = !isCalendarMode;
    const isDateValue = isInputDateValue(value);
    const numericDatePlaceholder = getNumericDateInputPlaceholder();
    const isCalendarDateValue = isDateValue && (isCalendarMode || textValueMode === 'date');
    const parsedTextDateValue = isFreeTextMode
      ? parseDateInputValue(value)
      : isFormattedDateMode
        ? parseNumericDateInput(value)
        : undefined;
    const parsedFormattedDraftValue =
      isFormattedDateMode && formattedDraftValue ? parseNumericDateInput(formattedDraftValue) : undefined;
    const calendarValue = parsedFormattedDraftValue ?? (isDateValue ? value : (parsedTextDateValue ?? ''));
    const displayValue = isFormattedDateMode
      ? (formattedDraftValue ?? formatNumericDateInput(value))
      : parsedTextDateValue
        ? formatNumericDateInput(parsedTextDateValue)
        : isCalendarDateValue
          ? formatDisplayDate(value)
          : value;
    const rangeErrorValue = formattedDraftValue ?? value;
    const isRangeError = isDateInputOutOfRange(rangeErrorValue, min, max);

    const openPicker = () => {
      if (!disabled) {
        updateCalendarPosition();
        setOpen(true);
      }
    };

    const closePicker = (nextValue?: string) => {
      setOpen(false);
      onBlur();
      if (nextValue !== undefined) {
        onValueBlur?.(nextValue);
      }
    };

    const handleChange = (nextValue: string) => {
      setTextValueMode('date');
      setFormattedDraftValue(undefined);
      onChange(nextValue);
      closePicker(nextValue);
    };

    const handleClear = () => {
      setTextValueMode('date');
      setFormattedDraftValue(undefined);
      onChange('');
      closePicker('');
    };

    const commitFormattedDraft = () => {
      if (!isFormattedDateMode || formattedDraftValue === undefined) return value;

      const parsedDateValue = parseNumericDateInput(formattedDraftValue);
      const nextValue = parsedDateValue ?? formattedDraftValue;
      setTextValueMode(parsedDateValue ? 'date' : 'freeText');
      setFormattedDraftValue(undefined);
      onChange(nextValue);

      return nextValue;
    };

    const handleTextChange = (nextValue: string) => {
      if (isFormattedDateMode) {
        const formattedValue = formatNumericDateInputDraft(nextValue);
        if (!isNumericDateInputDraftValid(formattedValue)) return;

        const parsedDateValue = parseNumericDateInput(formattedValue);
        if (parsedDateValue) {
          setTextValueMode('date');
          setFormattedDraftValue(undefined);
          onChange(parsedDateValue);
          return;
        }

        setTextValueMode(parsedDateValue ? 'date' : 'freeText');
        setFormattedDraftValue(formattedValue);
        return;
      }

      setFormattedDraftValue(undefined);
      setTextValueMode('freeText');
      onChange(nextValue);
    };

    const handleNumericDateKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (!isFormattedDateMode) return;
      if (event.key === 'Enter') {
        commitFormattedDraft();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey || NUMERIC_DATE_ALLOWED_KEYS.has(event.key)) return;
      if (/^\d$/.test(event.key)) return;
      if (event.key === '.') return;

      event.preventDefault();
    };

    const handleTextBlur = () => {
      const nextValue = commitFormattedDraft();
      onBlur();
      onValueBlur?.(nextValue);
    };

    const calendar =
      open && !disabled && calendarStyle
        ? createPortal(
            <div ref={calendarRef} style={calendarStyle}>
              <GermanDateCalendar
                value={calendarValue}
                min={min}
                max={max}
                onChange={handleChange}
                onClear={handleClear}
              />
            </div>,
            document.body,
          )
        : null;

    return (
      <>
        <div ref={containerRef} style={{ width: '100%' }}>
          <Input
            type="text"
            variant={variant}
            readOnly={isCalendarMode}
            disabled={disabled}
            value={displayValue}
            placeholder={isFormattedDateMode ? numericDatePlaceholder : undefined}
            color={isRangeError ? 'danger' : undefined}
            onChange={isTextEditable ? (event) => handleTextChange(event.target.value) : undefined}
            onBlur={isTextEditable ? handleTextBlur : undefined}
            onClick={isCalendarMode ? openPicker : undefined}
            onKeyDown={handleNumericDateKeyDown}
            slotProps={
              isFormattedDateMode
                ? {
                    input: {
                      inputMode: 'numeric',
                      maxLength: numericDatePlaceholder.length,
                      pattern: '[0-9.]*',
                    },
                  }
                : undefined
            }
            sx={{ width: '100%', cursor: disabled ? undefined : isTextEditable ? 'text' : 'pointer' }}
            endDecorator={
              <>
                {showClearButton && (isDateValue || !!formattedDraftValue) && (
                  <IconButton
                    variant="plain"
                    color="neutral"
                    disabled={disabled}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}>
                    <Icons.Close />
                  </IconButton>
                )}
                <IconButton
                  variant="plain"
                  color="neutral"
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    openPicker();
                  }}>
                  <Icons.CalendarMonth />
                </IconButton>
              </>
            }
          />
        </div>
        {calendar}
      </>
    );
  };

  if (control && name) {
    return (
      <Controller
        control={control}
        name={name}
        rules={withDateRangeValidation(rules, min, max)}
        render={({ field: { value, onChange, onBlur } }) => renderInput((value as string) ?? '', onChange, onBlur)}
      />
    );
  }

  return renderInput(controlledValue ?? '', (value) => onControlledChange?.(value));
}
